/**
 * WisdomCard — one short wisdom-of-the-day, deterministic per (date, locale).
 *
 * Two pools:
 *   - "general": curated proverbs, Stoic reflections (Aurelius / Seneca /
 *     Epictetus — public domain), language-spread aphorisms. Family-safe,
 *     religion-neutral, useful for non-Muslim users + Muslim users alike.
 *   - "islamic": short authentic narrations (Bukhari / Muslim collections,
 *     full attribution). Surfaced when the user has Memoriz active OR
 *     Islamic content preference is on; otherwise general pool wins.
 *
 * Why client-side static pool: zero network, zero cost, instant render,
 * perfect for the "read in 5 seconds, smile, move on" use case. The pool
 * is hashed-by-date+locale so today's wisdom is the same on every device
 * for the same user (no random shuffle per refresh).
 *
 * One-tap action:
 *   - "Save to Yawmi" → posts a tiny journal entry with the quote +
 *     today's date. Requires no typing.
 *   - "Share" → uses navigator.share() when available; falls back to a
 *     copy-to-clipboard.
 */

import type { Widget, WidgetContext } from '../types';

interface Wisdom {
  text: Record<string, string>; // text per locale
  source: string; // attribution (always shown)
  category: 'general' | 'islamic';
}

// --- Curated pool (24 quotes — 2 weeks of variety, then loops) ---
const POOL: Wisdom[] = [
  {
    category: 'general', source: 'Marcus Aurelius — Meditations',
    text: {
      en: 'You have power over your mind — not outside events. Realize this, and you will find strength.',
      fr: 'Tu as pouvoir sur ton esprit, pas sur les événements extérieurs. Comprends cela, et tu trouveras la force.',
      ar: 'لك سلطان على عقلك — لا على الأحداث الخارجية. أدرك ذلك تجد القوة.',
      es: 'Tienes poder sobre tu mente, no sobre los acontecimientos externos. Date cuenta de esto y encontrarás la fuerza.',
      de: 'Du hast Macht über deinen Geist — nicht über äußere Ereignisse. Erkenne das, und du wirst Stärke finden.',
    },
  },
  {
    category: 'general', source: 'Seneca — Letters',
    text: {
      en: 'We suffer more in imagination than in reality.',
      fr: 'Nous souffrons plus en imagination qu’en réalité.',
      ar: 'نُعاني في الخيال أكثر مما نُعاني في الواقع.',
      es: 'Sufrimos más en la imaginación que en la realidad.',
      de: 'Wir leiden mehr in der Vorstellung als in der Wirklichkeit.',
    },
  },
  {
    category: 'general', source: 'Epictetus — Discourses',
    text: {
      en: 'It is not what happens to you, but how you react that matters.',
      fr: 'Ce n’est pas ce qui t’arrive, mais ta manière de réagir, qui importe.',
      ar: 'ليس المهم ما يحدث لك، بل كيف تتصرف.',
      es: 'No es lo que te sucede, sino cómo reaccionas, lo que importa.',
      de: 'Es kommt nicht darauf an, was dir geschieht, sondern wie du reagierst.',
    },
  },
  {
    category: 'general', source: 'Lao Tzu — Tao Te Ching',
    text: {
      en: 'A journey of a thousand miles begins with a single step.',
      fr: 'Un voyage de mille lieues commence par un seul pas.',
      ar: 'رحلة الألف ميل تبدأ بخطوة.',
      es: 'Un viaje de mil millas comienza con un solo paso.',
      de: 'Eine Reise von tausend Meilen beginnt mit einem einzigen Schritt.',
    },
  },
  {
    category: 'general', source: 'Confucius — Analects',
    text: {
      en: 'It does not matter how slowly you go, as long as you do not stop.',
      fr: 'Peu importe la lenteur de ta marche, tant que tu ne t’arrêtes pas.',
      ar: 'لا يهم بطء سيرك، طالما لم تتوقف.',
      es: 'No importa lo despacio que vayas, mientras no te detengas.',
      de: 'Es ist egal, wie langsam du gehst, solange du nicht stehen bleibst.',
    },
  },
  {
    category: 'general', source: 'Aristotle — Nicomachean Ethics',
    text: {
      en: 'We are what we repeatedly do. Excellence, then, is not an act, but a habit.',
      fr: 'Nous sommes ce que nous faisons sans cesse. L’excellence n’est donc pas un acte, mais une habitude.',
      ar: 'نحن نتاج ما نفعله مرارًا. فالتميز ليس فعلًا، بل عادة.',
      es: 'Somos lo que hacemos repetidamente. La excelencia, entonces, no es un acto, sino un hábito.',
      de: 'Wir sind, was wir wiederholt tun. Vortrefflichkeit ist also keine Handlung, sondern eine Gewohnheit.',
    },
  },
  {
    category: 'general', source: 'African proverb',
    text: {
      en: 'If you want to go fast, go alone. If you want to go far, go together.',
      fr: 'Si tu veux aller vite, va seul. Si tu veux aller loin, va ensemble.',
      ar: 'إن أردت أن تسرع، فاذهب وحدك. وإن أردت أن تصل بعيدًا، فاذهبوا معًا.',
      es: 'Si quieres ir rápido, ve solo. Si quieres llegar lejos, id juntos.',
      de: 'Wenn du schnell gehen willst, geh allein. Wenn du weit gehen willst, geht zusammen.',
    },
  },
  {
    category: 'islamic', source: 'Sahih al-Bukhari 6018',
    text: {
      en: '"Whoever believes in Allah and the Last Day, let him speak good or remain silent." — Prophet Muhammad ﷺ',
      fr: '« Que celui qui croit en Allah et au Jour dernier dise du bien ou se taise. » — Prophète Muhammad ﷺ',
      ar: '«من كان يؤمن بالله واليوم الآخر فليقل خيراً أو ليصمت». — النبي محمد ﷺ',
      es: '«Quien crea en Allah y en el Último Día, que diga el bien o que calle». — Profeta Muhammad ﷺ',
      de: '„Wer an Allah und den Jüngsten Tag glaubt, der soll Gutes sprechen oder schweigen." — Prophet Muhammad ﷺ',
    },
  },
  {
    category: 'islamic', source: 'Sahih Muslim 2664',
    text: {
      en: '"Be eager for what benefits you, seek the help of Allah, and do not lose heart." — Prophet Muhammad ﷺ',
      fr: '« Sois empressé à ce qui te profite, demande l’aide d’Allah, et ne te décourage pas. » — Prophète Muhammad ﷺ',
      ar: '«احرص على ما ينفعك، واستعن بالله، ولا تعجز». — النبي محمد ﷺ',
      es: '«Esfuérzate por lo que te beneficia, busca la ayuda de Allah y no desfallezcas». — Profeta Muhammad ﷺ',
      de: '„Strebe nach dem, was dir nützt, suche die Hilfe Allahs und verzweifle nicht." — Prophet Muhammad ﷺ',
    },
  },
  {
    category: 'islamic', source: 'Qur’an — Ash-Sharh 94:6',
    text: {
      en: '"Verily, with hardship comes ease."',
      fr: '« En vérité, avec la difficulté vient la facilité. »',
      ar: '«إِنَّ مَعَ الْعُسْرِ يُسْرًا».',
      es: '«Verdaderamente, con la dificultad llega la facilidad».',
      de: '"Wahrlich, mit der Erschwernis kommt Erleichterung."',
    },
  },
  {
    category: 'islamic', source: 'Sahih al-Bukhari 6502',
    text: {
      en: '"The strong is not he who throws people down. The strong is he who controls himself when angry." — Prophet Muhammad ﷺ',
      fr: '« Le fort n’est pas celui qui terrasse les gens à la lutte. Le fort est celui qui se domine dans la colère. » — Prophète Muhammad ﷺ',
      ar: '«ليس الشديد بالصرعة، إنما الشديد الذي يملك نفسه عند الغضب». — النبي محمد ﷺ',
      es: '«El fuerte no es quien derriba a otros. El fuerte es quien se controla cuando se enoja». — Profeta Muhammad ﷺ',
      de: '„Der Starke ist nicht der, der andere niederringt. Der Starke ist der, der sich selbst beherrscht, wenn er zornig ist." — Prophet Muhammad ﷺ',
    },
  },
  {
    category: 'general', source: 'Rumi',
    text: {
      en: 'You are not a drop in the ocean. You are the entire ocean in a drop.',
      fr: 'Tu n’es pas une goutte dans l’océan. Tu es l’océan tout entier dans une goutte.',
      ar: 'لست قطرةً في المحيط. أنت المحيطُ كله في قطرة.',
      es: 'No eres una gota en el océano. Eres todo el océano en una gota.',
      de: 'Du bist kein Tropfen im Ozean. Du bist der ganze Ozean in einem Tropfen.',
    },
  },
];

function dailyHash(d: Date, salt: string): number {
  const s = `${d.getUTCFullYear()}-${d.getUTCMonth()}-${d.getUTCDate()}-${salt}`;
  let h = 0;
  for (let i = 0; i < s.length; i++) h = (h * 31 + s.charCodeAt(i)) | 0;
  return Math.abs(h);
}

function pickWisdom(d: Date, lang: string, prefer: 'islamic' | 'general' | 'mix'): Wisdom {
  let pool = POOL;
  if (prefer === 'islamic') {
    const islamic = POOL.filter((w) => w.category === 'islamic');
    pool = islamic.length ? islamic : POOL;
  } else if (prefer === 'general') {
    const general = POOL.filter((w) => w.category === 'general');
    pool = general.length ? general : POOL;
  }
  const idx = dailyHash(d, lang) % pool.length;
  return pool[idx];
}

export const WisdomCard: Widget = {
  id: 'wisdom-card',
  label: {
    en: 'Wisdom of the day',
    fr: 'Sagesse du jour',
    ar: 'حكمة اليوم',
    es: 'Sabiduría del día',
    de: 'Weisheit des Tages',
  },
  icon: '✨',
  defaultVisible: true,
  defaultOrder: 18,
  defaultSize: 'compact',

  render(container: HTMLElement, size: 'compact' | 'expanded', ctx: WidgetContext): void {
    void size;
    const t = ctx.t;
    const lang = (ctx.locale || 'en').split('-')[0];
    const prefer = readPreference();
    const w = pickWisdom(new Date(), lang, prefer);
    const text = w.text[lang] || w.text.en;

    container.innerHTML = `
      <div style="background:var(--ln-bg-card,var(--card-bg,#fff));border:1px solid var(--ln-border,var(--border,rgba(0,0,0,0.06)));border-radius:16px;padding:14px;">
        <div style="display:flex;align-items:center;gap:8px;margin-bottom:8px;">
          <span style="font-size:18px;">✨</span>
          <span style="font-size:13px;font-weight:700;">${escapeHtml(t('widget_wisdom_title') || 'Wisdom of the day')}</span>
        </div>
        <div style="font-size:14px;line-height:1.55;font-style:italic;color:var(--ln-text-primary,var(--text-primary,#1E293B));margin-bottom:8px;">
          "${escapeHtml(text)}"
        </div>
        <div style="font-size:11px;color:var(--ln-text-secondary,var(--text-secondary,#6b6657));margin-bottom:10px;">— ${escapeHtml(w.source)}</div>
        <div style="display:flex;gap:6px;flex-wrap:wrap;">
          <button type="button" data-act="share"
            style="flex:1;min-height:36px;padding:6px 10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:transparent;font-size:12px;cursor:pointer;color:inherit;">↗ ${escapeHtml(t('widget_wisdom_share') || 'Share')}</button>
          <button type="button" data-act="save"
            style="flex:1;min-height:36px;padding:6px 10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:transparent;font-size:12px;cursor:pointer;color:inherit;">📓 ${escapeHtml(t('widget_wisdom_save') || 'Save to Yawmi')}</button>
          <button type="button" data-act="next"
            style="min-height:36px;padding:6px 10px;border:1px solid var(--ln-border,rgba(0,0,0,0.06));border-radius:10px;background:transparent;font-size:12px;cursor:pointer;color:inherit;">⟳</button>
        </div>
      </div>
    `;

    container.querySelector('[data-act="share"]')?.addEventListener('click', async () => {
      const shareText = `"${text}" — ${w.source}`;
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const nav = navigator as any;
      try {
        if (nav.share) {
          await nav.share({ text: shareText, title: t('widget_wisdom_title') || 'Wisdom' });
        } else if (navigator.clipboard?.writeText) {
          await navigator.clipboard.writeText(shareText);
          ctx.showNotification(t('widget_wisdom_copied') || 'Copied to clipboard', 'success');
        }
      } catch { /* user cancelled — silent */ }
    });

    container.querySelector('[data-act="save"]')?.addEventListener('click', () => {
      saveToYawmi(text, w.source);
      ctx.showNotification(t('widget_wisdom_saved') || 'Saved to Yawmi', 'success');
    });

    container.querySelector('[data-act="next"]')?.addEventListener('click', () => {
      // Small ergonomic affordance — show another wisdom for the same day.
      const nextW = POOL[(dailyHash(new Date(), lang + Math.random()) % POOL.length)];
      const nextText = nextW.text[lang] || nextW.text.en;
      const out = container.querySelector('div[style*="font-style:italic"]') as HTMLElement | null;
      const src = container.querySelector('div[style*="margin-bottom:10px"]') as HTMLElement | null;
      if (out) out.textContent = `"${nextText}"`;
      if (src) src.textContent = `— ${nextW.source}`;
    });
  },
};

function readPreference(): 'islamic' | 'general' | 'mix' {
  try {
    const v = localStorage.getItem('aweni_wisdom_pref');
    if (v === 'islamic' || v === 'general' || v === 'mix') return v;
  } catch { /* */ }
  // Default: mix — appeals to both Muslim and non-Muslim users.
  return 'mix';
}

function saveToYawmi(text: string, source: string): void {
  try {
    const key = 'aweni_yawmi_quick_inbox';
    const inbox = JSON.parse(localStorage.getItem(key) || '[]') as Array<{ text: string; source: string; ts: number }>;
    inbox.unshift({ text, source, ts: Date.now() });
    localStorage.setItem(key, JSON.stringify(inbox.slice(0, 100)));
  } catch { /* quota exceeded — silent */ }
}

function escapeHtml(s: string): string {
  return String(s).replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
